import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { api, User, type ApiError } from "@/lib/api";

// Last confirmed profile (no secrets), used to keep the session usable when
// /auth/me fails transiently (rate limit, 5xx, network).
const USER_CACHE_KEY = 'auth_user';

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: User | null) {
  if (!user) {
    localStorage.removeItem(USER_CACHE_KEY);
    return;
  }
  const { id, username, email, role, isActive } = user;
  localStorage.setItem(USER_CACHE_KEY, JSON.stringify({ id, username, email, role, isActive }));
}

/** Only an explicit auth rejection ends the session; everything else is retried. */
const isAuthRejection = (error: unknown) => {
  const status = (error as ApiError)?.status;
  return status === 401 || status === 403;
};

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  /** Merge server-confirmed profile changes into the session user. */
  updateUser: (changes: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!api.isAuthenticated()) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let warned = false;
    let inFlight = false;

    const checkAuth = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      clearTimeout(timer);
      try {
        const data = await api.get('/auth/me');
        if (!data?.user) throw new Error('Invalid /auth/me response');
        if (cancelled) return;
        const currentUser = data.user as User;
        setUser(currentUser);
        writeCachedUser(currentUser);
        if (currentUser.apiKey) {
          api.setApiKey(currentUser.apiKey);
        } else {
          console.warn("User authenticated but no API key found in profile");
        }
        setIsLoading(false);
        window.removeEventListener('focus', checkAuth);
        window.removeEventListener('online', checkAuth);
      } catch (error) {
        if (cancelled) return;
        if (isAuthRejection(error)) {
          toast.error('Your session has expired. Please log in again.');
          api.logout();
          writeCachedUser(null);
          setUser(null);
          setIsLoading(false);
          return;
        }
        // Transient (429 / 5xx / network): keep the token and the last known
        // profile so the user stays where they are, and retry in the background.
        logger.log('Transient /auth/me failure, retrying', error);
        const cached = readCachedUser();
        if (cached) {
          setUser((prev) => prev ?? { ...cached, apiKey: api.getApiKey() ?? undefined });
          setIsLoading(false);
        }
        if (!warned) {
          warned = true;
          toast.warning("The server is busy. Retrying…");
        }
        timer = setTimeout(checkAuth, Math.min(30_000, 2_000 * 2 ** attempt++));
      } finally {
        inFlight = false;
      }
    };

    window.addEventListener('focus', checkAuth);
    window.addEventListener('online', checkAuth);
    checkAuth();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('focus', checkAuth);
      window.removeEventListener('online', checkAuth);
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const { user, token } = await api.login(email, password);
      
      // Store the user and the API key
      setUser(user);
      writeCachedUser(user);
      if (user.apiKey) {
        api.setApiKey(user.apiKey);
      } else {
        console.warn("Logged in but no API key found in user object");
      }
      
      toast.success(`Welcome back, ${user.username}!`);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please check your credentials.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    api.logout();
    // Drop the cached gateway keys so they don't outlive the session.
    api.setApiKey('');
    localStorage.removeItem('api_key');
    localStorage.removeItem('apiKeys');
    writeCachedUser(null);
    setUser(null);
    toast.info('You have been logged out.');
  };

  const updateUser = useCallback((changes: Partial<User>) => {
    setUser((prev) => {
      const next = prev ? { ...prev, ...changes } : prev;
      if (next) writeCachedUser(next);
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider 
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context module exports provider + hook by design
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 